import { useEffect } from "react";
import { useSmoothScrollLenis } from "../components/common/SmoothScroll";

// Why this exists: data-lenis-prevent (see SmoothScroll.jsx / index.css's
// [data-lenis-prevent] { overscroll-behavior: contain }) only tells Lenis
// "don't smooth-hijack a wheel/touch gesture that starts here — let it
// scroll natively instead." That's correct and sufficient for an element
// that has its OWN scrollable overflow (a modal's body, a dropdown list):
// the native scroll it falls back to stays local, inside that element.
// It does nothing for a full-viewport backdrop with no overflow of its
// own (most of this app's modal overlays) — native scroll finds no closer
// scrollable ancestor and falls through to the document itself. Confirmed
// empirically: adding data-lenis-prevent to a backdrop did not stop wheel
// input over it from moving the page underneath. A real, App-level lock is
// the only thing that closes that gap.
//
// Reference-counted at module scope so N simultaneously-open overlays
// require N releases before the page unlocks — closing one of several
// open overlays must not free the page while another is still showing.
let lockCount = 0;
let savedBodyStyle = null;
let savedScrollY = 0;

// Which Lenis instance (if any) THIS system stopped — tracked so a final
// release only ever resumes an instance it actually paused itself, never
// one a different feature already had stopped for its own reason, and
// never a stale instance that's since been destroyed/replaced (e.g. by a
// prefers-reduced-motion toggle while an overlay is open).
let stoppedByLock = null;

const BODY_STYLE_PROPS = ["position", "top", "left", "right", "width", "overflow", "paddingRight"];

function engageLock() {
  if (lockCount === 0) {
    savedScrollY = window.scrollY;
    savedBodyStyle = {};
    for (const prop of BODY_STYLE_PROPS) savedBodyStyle[prop] = document.body.style[prop];

    // position:fixed (not overflow:hidden alone) — overflow:hidden on body
    // is inconsistently honored across browsers (notably iOS Safari still
    // rubber-bands the background) and doesn't touch keyboard-triggered
    // document scroll at all. A fixed body can't scroll by any input
    // method, full stop, with no touch-event listeners required. The
    // negative top offset keeps the page LOOKING exactly as scrolled as it
    // already was while it's pinned.
    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    // This app hides scrollbars globally (index.css: scrollbar-width: none
    // + ::-webkit-scrollbar { display: none }), so scrollbarGap is 0 in
    // practice today — computed properly anyway so this stays correct if
    // that ever changes, rather than silently reintroducing a shift.
    if (scrollbarGap > 0) {
      const existing = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${existing + scrollbarGap}px`;
    }
  }
  lockCount++;
}

function releaseLock() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0 && savedBodyStyle) {
    for (const prop of BODY_STYLE_PROPS) document.body.style[prop] = savedBodyStyle[prop];
    savedBodyStyle = null;
    window.scrollTo({ top: savedScrollY, left: 0, behavior: "instant" });
  }
}

/**
 * Locks page scroll while `isActive` is true, for as long as this
 * component stays mounted with it true. Pass the modal/drawer/sheet's own
 * open state — not a manual open/close call — so unmounting (including an
 * unexpected one, e.g. a route change while open) always releases via the
 * effect's cleanup, the same way React guarantees any other effect cleans
 * up.
 *
 * Only call this from TRUE overlays (a fixed backdrop stacked over the
 * page) — never from an inline dropdown/suggestion list/expandable panel,
 * which already scrolls correctly via data-lenis-prevent alone and has no
 * backdrop for a page-level lock to protect against.
 */
export function useBodyScrollLock(isActive) {
  const lenis = useSmoothScrollLenis();

  // Body-style lock/unlock is intentionally its own effect, keyed only on
  // isActive. Its cleanup (releaseLock) always runs before the Lenis
  // effect's cleanup below, since React runs a component's effect
  // cleanups/setups in declaration order — that ordering is load-bearing:
  // it's what lets the Lenis effect read an already-up-to-date lockCount.
  useEffect(() => {
    if (typeof document === "undefined" || !isActive) return undefined;
    engageLock();
    return () => releaseLock();
  }, [isActive]);

  // Deliberately depends on `lenis` too (not just isActive) so a Lenis
  // instance replacement while still locked — e.g. a prefers-reduced-motion
  // toggle destroying/recreating the instance mid-overlay — re-runs this
  // effect on its own, without touching the body-lock effect above. That
  // keeps engageLock/releaseLock from ever firing on an instance swap,
  // which would otherwise briefly unlock the body and re-capture scroll.
  useEffect(() => {
    if (typeof document === "undefined" || !isActive || !lenis) return undefined;

    // Formally pauses Lenis's own wheel/touch handling while locked — the
    // modal's own scrollable content is already independent of Lenis via
    // data-lenis-prevent, so this only affects the (now doubly-protected)
    // background. Only stop (and claim ownership of resuming) an instance
    // that isn't already stopped — if something else already paused it, or
    // a nested lock's own instance is already ours, leave it exactly as is.
    if (!lenis.isStopped) {
      try {
        lenis.stop();
        stoppedByLock = lenis;
      } catch (err) {
        console.warn("useBodyScrollLock: lenis.stop() failed; body lock is unaffected.", err);
      }
    }

    return () => {
      // lockCount is read live here, after releaseLock()'s cleanup above
      // has already run for a genuine final release, so this correctly
      // sees 0 only when no overlay remains open. If this cleanup instead
      // fires because `lenis` itself changed while still locked, lockCount
      // is untouched (still > 0) and this intentionally does nothing — the
      // new effect run immediately takes over stopping the new instance.
      // stoppedByLock === lenis further guards against resuming an
      // instance this system never stopped (already-stopped-elsewhere) or
      // one that's since been replaced.
      if (lockCount === 0 && stoppedByLock === lenis) {
        try {
          lenis.start();
        } catch (err) {
          console.warn("useBodyScrollLock: lenis.start() failed.", err);
        } finally {
          stoppedByLock = null;
        }
      }
    };
  }, [isActive, lenis]);
}
